import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, Spinner, Tag, TextInput } from '@opendate-app/setlist';
import AreaPlacesList, { validPlaces } from './AreaPlacesList';
import { ColorPicker } from '../../../shared';
import { DEFAULT_HOLD_COLOR } from '../../../shared/seatsioUtils';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import Flatpickr from 'react-flatpickr';
import { offsetDateForBrowser, offsetDateForServer } from '../../../shared/timeZoneLogic';

const SelectedList = ({ selected, onRemoveLabel, labelToCategory }) => (
  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
    {(selected || []).map(label => (
      <Tag key={label} size="sm" onDismiss={() => onRemoveLabel(label)}>
        {labelToCategory?.[label] ? `${labelToCategory[label]}: ${label}` : label}
      </Tag>
    ))}
  </div>
);

SelectedList.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string),
  onRemoveLabel: PropTypes.func.isRequired,
  labelToCategory: PropTypes.objectOf(PropTypes.string)
};

SelectedList.defaultProps = {
  selected: null,
  labelToCategory: null
};

const isPlaceArea = (object) => object && object.objectType === 'GeneralAdmissionArea' && !object.bookAsAWhole;

const followMap = (area, onMap) => (area.syncing
  ? { ...area, onMap }
  : { ...area, onMap, places: (area.base || 0) + onMap });

const without = (obj, key) => {
  const copy = { ...(obj || {}) };
  delete copy[key];
  return copy;
};

const HoldModal = ({
  open,
  onClose,
  title,
  seatsIOPublicKey,
  channels,
  selectedEvent,
  initialHold,
  onSubmit,
  renderRelease,
  takenObjects,
  allowAreaPlaces
}) => {
  const chartRef = useRef(null);
  const takenRef = useRef(takenObjects);
  takenRef.current = takenObjects;
  const [takenNotice, setTakenNotice] = useState(null);
  const [hold, setHold] = useState({ name: '', objects: [], color: '', release_at: null });
  const holdRef = useRef(hold);
  holdRef.current = hold;
  const [isSaving, setIsSaving] = useState(false);
  const [labelToCategory, setLabelToCategory] = useState({});
  const [gaAreas, setGaAreas] = useState({});
  const [pendingAreas, setPendingAreas] = useState(null);
  const [areas, setAreas] = useState({});
  const areasRef = useRef(areas);
  areasRef.current = areas;

  useEffect(() => {
    if (!open) return;
    const initialPlaces = allowAreaPlaces ? (initialHold?.area_places || {}) : {};
    setAreas(Object.fromEntries(Object.entries(initialPlaces).map(([label, places]) => (
      [label, { places, onMap: 0, base: Number(places) || 0, syncing: true, whole: false, initial: Number(places) || 0, free: null }]
    ))));
    setHold({
      name: initialHold?.name || '',
      objects: (initialHold?.objects || []).filter(label => !(label in initialPlaces)),
      color: initialHold?.color || DEFAULT_HOLD_COLOR,
      release_at: initialHold?.release_at ? offsetDateForBrowser(initialHold.release_at) : null,
      release_offset_minutes: initialHold?.release_offset_minutes ?? null,
    });
    setLabelToCategory({});
    setGaAreas({});
    setPendingAreas(null);
    setTakenNotice(null);
  }, [open, JSON.stringify(initialHold || {})]);

  const settle = (chart, labels) => chartRef.current === chart && setAreas(prev => Object.fromEntries(Object.entries(prev).map(([label, area]) => (
    [label, labels.includes(label) && area.syncing ? (area.initial > 0
      ? { ...area, syncing: false, places: Math.max(Number(area.places) || 0, area.onMap), base: Math.max((Number(area.places) || 0) - area.onMap, 0) }
      : { ...area, syncing: false, places: area.onMap, base: 0 }) : area]
  ))));

  const areaMax = ({ free, initial }) => (free == null ? null : free + (initial || 0));
  const areaRows = Object.entries(areas).map(([label, area]) => ({ label, places: area.places, whole: area.whole, max: areaMax(area), heldPlaces: area.initial > 0, busy: Boolean(area.syncing) }));
  const keepsPlaces = row => row.whole && row.heldPlaces && row.max != null;
  const partialRows = areaRows.filter(row => !row.whole).concat(areaRows.filter(keepsPlaces).map(row => ({ ...row, places: row.max })));
  const wholeLabels = areaRows.filter(row => row.whole && !keepsPlaces(row)).map(row => row.label);
  const totalSelected = (hold.objects || []).length + wholeLabels.length + partialRows.reduce((sum, row) => sum + (Number(row.places) || 0), 0);
  const placesInvalid = partialRows.some(row => !validPlaces(row.places, row.max));
  const isSaveDisabled = isSaving || !hold.name || totalSelected === 0 || placesInvalid;

  const handleSave = () => {
    if (isSaveDisabled) return;
    const fullAreas = (hold.objects || [])
      .filter(label => label in gaAreas && !(initialHold?.objects || []).includes(label))
      .map(label => ({ label, free: gaAreas[label] }));
    if (fullAreas.length > 0) {
      setPendingAreas(fullAreas);
      return;
    }
    submit();
  };

  const submit = () => {
    setPendingAreas(null);
    if (isSaveDisabled) return;
    setIsSaving(true);
    onSubmit({
      name: hold.name,
      objects: [...(hold.objects || []), ...wholeLabels],
      ...(allowAreaPlaces && { area_places: Object.fromEntries(partialRows.map(row => [row.label, Number(row.places)])) }),
      color: hold.color || DEFAULT_HOLD_COLOR,
      ...(renderRelease
        ? { release_offset_minutes: hold.release_offset_minutes }
        : { release_at: hold.release_at ? offsetDateForServer(hold.release_at) : null }),
    })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal open={open} onClose={onClose} title={title} width="95vw" data-above-sidebar>
      <div className="d-flex flex-column flex-md-row" style={{ width: '100%', height: 'calc(95vh - 110px)', minHeight: 0 }}>
        <div className="flex-fill d-flex" style={{ minWidth: 0, minHeight: 0, height: '100%' }}>
          <div className="w-100 h-100">
            <SeatsioSeatingChart
              workspaceKey={seatsIOPublicKey}
              channels={channels}
              event={selectedEvent && selectedEvent.seatsio_event_id}
              onRenderStarted={chart => {
                chartRef.current = chart;
                chart.config.onObjectSelected = (object) => {
                  const taken = takenRef.current && takenRef.current[object.label];
                  if (taken) {
                    chart.deselectObjects([object.label]);
                    setTakenNotice(`${object.label} is already in ${taken.owner}.`);
                    return;
                  }
                  setTakenNotice(null);
                  setPendingAreas(null);
                  if (allowAreaPlaces && isPlaceArea(object)) {
                    const free = object.numFree ?? null;
                    const heldWhole = (holdRef.current.objects || []).includes(object.label);
                    const onMap = Number(object.numSelected) || 1;
                    setAreas(prev => (prev[object.label]
                      ? { ...prev, [object.label]: { ...followMap(prev[object.label], onMap), free: prev[object.label].free ?? free } }
                      : { ...prev, [object.label]: { places: onMap, onMap, base: 0, whole: heldWhole, initial: 0, free } }));
                    if (heldWhole) {
                      setHold(prev => ({ ...prev, objects: (prev.objects || []).filter(l => l !== object.label) }));
                    }
                    setLabelToCategory(prev => ({ ...(prev || {}), [object.label]: object.category?.label }));
                    return;
                  }
                  setHold(prev => ({
                    ...prev,
                    objects: Array.from(new Set([...(prev.objects || []), object.label]))
                  }));
                  if (object.objectType === "GeneralAdmissionArea") {
                    setGaAreas(prev => ({ ...prev, [object.label]: object.numFree ?? object.capacity }));
                  }
                  if (object && object.label) {
                    setLabelToCategory(prev => ({ ...(prev || {}), [object.label]: object.category?.label }));
                  }
                };
                chart.config.onObjectDeselected = (object) => {
                  if (object.label in areasRef.current) {
                    const onMap = Number(object.numSelected) || 0;
                    if (onMap > 0 || areasRef.current[object.label].base > 0) {
                      setAreas(prev => ({ ...prev, [object.label]: followMap(prev[object.label], onMap) }));
                      return;
                    }
                    setAreas(prev => without(prev, object.label));
                    setPendingAreas(null);
                    return;
                  }
                  setHold(prev => ({
                    ...prev,
                    objects: (prev.objects || []).filter(l => l !== object.label)
                  }));
                  setPendingAreas(null);
                  if (object && object.label) {
                    setLabelToCategory(prev => {
                      const copy = { ...(prev || {}) };
                      delete copy[object.label];
                      return copy;
                    });
                  }
                };
                const initialAreaPlaces = allowAreaPlaces ? (initialHold?.area_places || {}) : {};
                const initialSeats = (initialHold?.objects || []).filter(label => !(label in initialAreaPlaces));
                if (initialSeats.length > 0) {
                  chart.selectObjects(initialSeats);
                }
                const initialAreas = Object.entries(initialAreaPlaces).map(([id, places]) => ({ id, amount: Number(places) || 1 }));
                if (initialAreas.length > 0) {
                  new Promise(resolve => resolve(chart.selectObjects(initialAreas))).catch(() => null).then(() => settle(chart, initialAreas.map(({ id }) => id)));
                }
              }}
              objectColor={(object, defaultColor, extraConfig) => {
                const taken = extraConfig && extraConfig.taken && extraConfig.taken[object.label];
                return taken ? taken.color : (object.channel ? (object.channel.color || '#7f56d9') : undefined);
              }}
              {...(takenObjects && { extraConfig: { taken: takenObjects } })}
              region="na"
            />
          </div>
        </div>
        <div className="pl-md-4 pt-3 pt-md-0 d-flex flex-column h-100" style={{ width: '100%', maxWidth: 450, overflowY: 'auto' }}>
          <div className="mb-3">
            <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>Basic Details</div>
            <div style={{ fontSize: '14px', color: 'var(--sl-text-tertiary)' }}>Essential information regarding this hold.</div>
          </div>
          <div className="mb-3">
            <TextInput label="Hold Name" required value={hold.name} onChange={e => { setPendingAreas(null); setHold(prev => ({ ...prev, name: e.target.value })); }} />
          </div>
          <div className="form-row">
            <div className="col-6">
              <label style={{ color: 'var(--sl-text-secondary)' }}>Color</label>
              <ColorPicker value={hold.color} onChange={value => setHold(prev => ({ ...prev, color: value }))} inputPlaceholder="#6DE412" />
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2">
              <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>Inventory & Duration</div>
              <div style={{ fontSize: '14px', color: 'var(--sl-text-tertiary)' }}>The quantity of tickets on hold and an optional release date that can be set for this hold.</div>
            </div>
            <div className="form-row">
              <div className="col-6">
                <TextInput label="Ticket Count" value={String(totalSelected)} disabled readOnly />
              </div>
              <div className="col-6">
                {renderRelease ? renderRelease(hold.release_offset_minutes, value => setHold(prev => ({ ...prev, release_offset_minutes: value }))) : (
                <>
                <label className="mb-1" style={{ fontSize: '14px', color: 'var(--sl-text-secondary)' }}>Release Date</label>
                <Flatpickr
                  options={{ enableTime: true, dateFormat: 'F j, Y - h:i K', minDate: new Date() }}
                  placeholder="Select date / time"
                  className="form-control form-control-flatpickr"
                  value={hold.release_at || ''}
                  onClose={(dates) => setHold(prev => ({ ...prev, release_at: dates[0] }))}
                />
                </>
                )}
              </div>
            </div>
            <div className="my-2" style={{ fontSize: '12px', color: 'var(--sl-text-tertiary)' }}>
              Selected ({totalSelected})
            </div>
            {takenNotice && (
              <div className="my-2" role="status" style={{ fontSize: '13px', color: 'var(--sl-text-error, #d92d20)' }}>
                {takenNotice}
              </div>
            )}
          </div>
          <div className="mt-2 flex-grow-1 overflow-auto" style={{ minHeight: 160, background: 'var(--sl-bg-secondary)', borderRadius: 'var(--sl-radius-md)', padding: 16 }}>
            <SelectedList
              selected={hold.objects}
              labelToCategory={labelToCategory}
              onRemoveLabel={(label) => {
                setHold(prev => ({
                  ...prev,
                  objects: (prev.objects || []).filter(l => l !== label)
                }));
                setLabelToCategory(prev => {
                  const copy = { ...(prev || {}) };
                  delete copy[label];
                  return copy;
                });
                setPendingAreas(null);
                const chart = chartRef.current;
                chart.deselectObjects([label]);
              }}
            />
            {areaRows.length > 0 && (
              <AreaPlacesList
                areas={areaRows}
                onChangePlaces={(label, value) => {
                  setPendingAreas(null);
                  setAreas(prev => ({ ...prev, [label]: { ...prev[label], places: value } }));
                }}
                onCommitPlaces={(label) => {
                  const area = areasRef.current[label];
                  if (!area || area.syncing || !chartRef.current) return;
                  if (area.whole) return;
                  if (!validPlaces(area.places, areaMax(area))) return;
                  const target = Number(area.places);
                  const base = Math.min(area.base || 0, target);
                  const change = target - base - area.onMap;
                  if (change === 0) {
                    setAreas(prev => ({ ...prev, [label]: { ...prev[label], base } }));
                    return;
                  }
                  setAreas(prev => ({ ...prev, [label]: { ...prev[label], base, syncing: true } }));
                  const objects = [{ id: label, amount: Math.abs(change) }];
                  const chart = chartRef.current;
                  new Promise(resolve => resolve(change > 0 ? chart.selectObjects(objects) : chart.deselectObjects(objects))).catch(() => null).then(() => settle(chart, [label]));
                }}
                onToggleWhole={(label, whole) => {
                  setPendingAreas(null);
                  setAreas(prev => ({ ...prev, [label]: { ...prev[label], whole } }));
                }}
                onRemove={(label) => {
                  if (areasRef.current[label]?.syncing) return;
                  const onMap = areasRef.current[label]?.onMap || 0;
                  setPendingAreas(null);
                  setAreas(prev => without(prev, label));
                  if (onMap > 0) {
                    chartRef.current?.deselectObjects([{ id: label, amount: onMap }]);
                  }
                }}
              />
            )}
          </div>
          {pendingAreas && (
            <div className="mt-3">
              <Alert
                color="warning"
                size="full-width"
                role="alertdialog"
                title="Hold the whole area?"
                description={pendingAreas.map(({ label, free }) => (
                  <div key={label}>
                    {free == null
                      ? `This hold takes every open spot in ${label}. Nothing in ${label} will be for sale. Hold them all?`
                      : `This hold takes every open spot in ${label} (${free}). Nothing in ${label} will be for sale. Hold all ${free}?`}
                  </div>
                ))}
                actions={(
                  <>
                    <Button onClick={() => setPendingAreas(null)}>Cancel</Button>
                    <Button variant="primary" onClick={submit}>Confirm</Button>
                  </>
                )}
              />
            </div>
          )}
          {!pendingAreas && (
          <div className="d-flex justify-content-end mt-3 pt-3" style={{ gap: 12, borderTop: '1px solid var(--sl-border-secondary)' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={isSaveDisabled} onClick={() => handleSave()}>
              {isSaving ? <Spinner size="sm" color="currentColor" aria-label="Saving" /> : 'Save'}
            </Button>
          </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

HoldModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  seatsIOPublicKey: PropTypes.string.isRequired,
  channels: PropTypes.arrayOf(PropTypes.string),
  selectedEvent: PropTypes.object,
  initialHold: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderRelease: PropTypes.func,
  takenObjects: PropTypes.objectOf(PropTypes.shape({ owner: PropTypes.string, color: PropTypes.string })),
  allowAreaPlaces: PropTypes.bool
};

HoldModal.defaultProps = {
  selectedEvent: null,
  initialHold: null,
  renderRelease: null,
  takenObjects: null,
  channels: ['NO_CHANNEL'],
  allowAreaPlaces: false
};

export default HoldModal;
